import React, { useState, useMemo } from "react";
import {
  Card,
  Typography,
  Row,
  Col,
  DatePicker,
  Button,
  Space,
  Table,
  Statistic,
  Spin,
  Alert,
  message,
  Segmented,
  Divider,
  Tag,
} from "antd";
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DollarOutlined,
  UserOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { reportsService } from "../services/reportsService";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ReportsPage: React.FC = () => {
  const [reportType, setReportType] = useState("revenue");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, "days"),
    dayjs(),
  ]);
  const [exportLoading, setExportLoading] = useState(false);

  const startDate = dateRange[0]?.format("YYYY-MM-DD") || "";
  const endDate = dateRange[1]?.format("YYYY-MM-DD") || "";

  // ดึงข้อมูลจาก API
  const {
    data: revenueReport,
    isLoading: revenueLoading,
    error: revenueError,
  } = useQuery(
    ["revenue-report", startDate, endDate],
    () => reportsService.getRevenueReport({ startDate, endDate, groupBy: "day" }),
    {
      enabled: !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const {
    data: roomUsageReport,
    isLoading: roomUsageLoading,
    error: roomUsageError,
  } = useQuery(
    ["room-usage-report", startDate, endDate],
    () => reportsService.getRoomUsageReport({ startDate, endDate }),
    {
      enabled: !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const {
    data: employeePerformanceReport,
    isLoading: employeeLoading,
    error: employeeError,
  } = useQuery(
    ["employee-performance-report", startDate, endDate],
    () =>
      reportsService.getEmployeePerformanceReport({
        startDate,
        endDate,
        role: "therapist",
      }),
    {
      enabled: !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // เตรียมข้อมูล
  const revenueData = useMemo(() => {
    if (!revenueReport?.data?.groupedData) return [];
    return revenueReport.data.groupedData.map((item) => ({
      date: dayjs(item.date).format("DD/MM"),
      revenue: item.revenue.LAK,
      customers: item.receiptCount,
    }));
  }, [revenueReport]);

  const serviceData = useMemo(() => {
    if (!roomUsageReport?.data?.roomUsage) return [];
    return roomUsageReport.data.roomUsage.map((room, index) => ({
      name: room.room.roomName,
      count: room.totalServices,
      revenue: room.revenue.LAK,
      color: ["#667eea", "#52c41a", "#faad14", "#f5222d", "#13c2c2"][index % 5],
    }));
  }, [roomUsageReport]);

  const employeeData = useMemo(() => {
    if (!employeePerformanceReport?.data?.performance) return [];
    return employeePerformanceReport.data.performance.map((emp, index) => ({
      key: index,
      name: emp.employee.name,
      services: emp.totalServices,
      commission: emp.totalServices * 15000,
      satisfaction: Math.round(emp.customerSatisfactionAvg) || 0,
    }));
  }, [employeePerformanceReport]);

  const revenueStats = useMemo(() => {
    if (!revenueReport?.data?.summary) {
      return {
        totalRevenue: 0,
        totalCustomers: 0,
        avgRevenuePerDay: 0,
        avgRevenuePerCustomer: 0,
      };
    }
    const { totalRevenue, totalReceipts } = revenueReport.data.summary;
    const totalDays = dayjs(endDate).diff(dayjs(startDate), "day") + 1;
    return {
      totalRevenue: totalRevenue.LAK,
      totalCustomers: totalReceipts,
      avgRevenuePerDay: totalDays > 0 ? Math.round(totalRevenue.LAK / totalDays) : 0,
      avgRevenuePerCustomer:
        totalReceipts > 0 ? Math.round(totalRevenue.LAK / totalReceipts) : 0,
    };
  }, [revenueReport, startDate, endDate]);

  // Export Functions
  const exportToExcel = () => {
    if (!revenueData.length && !serviceData.length && !employeeData.length) {
      message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
      return;
    }

    setExportLoading(true);
    try {
      const workbook = XLSX.utils.book_new();

      const revenueSheetData = revenueData.map((item) => ({
        ວັນທີ: item.date,
        ລາຍຮັບ: new Intl.NumberFormat("lo-LA").format(item.revenue) + " ₭",
        ຈຳນວນລູກຄ້າ: item.customers,
      }));
      const revenueSheet = XLSX.utils.json_to_sheet([
        {
          ວັນທີ: "ສະຫຼຸບ",
          ລາຍຮັບ: new Intl.NumberFormat("lo-LA").format(revenueStats.totalRevenue) + " ₭",
          ຈຳນວນລູກຄ້າ: revenueStats.totalCustomers,
        },
        {},
        ...revenueSheetData,
      ]);
      XLSX.utils.book_append_sheet(workbook, revenueSheet, "ລາຍງານລາຍຮັບ");

      const serviceSheetData = serviceData.map((item) => ({
        ບໍລິການ: item.name,
        ຈຳນວນຄັ້ງ: item.count,
        ລາຍຮັບ: new Intl.NumberFormat("lo-LA").format(item.revenue) + " ₭",
      }));
      const serviceSheet = XLSX.utils.json_to_sheet(serviceSheetData);
      XLSX.utils.book_append_sheet(workbook, serviceSheet, "ລາຍງານບໍລິການ");

      const employeeSheetData = employeeData.map((item) => ({
        ຊື່: item.name,
        ຈຳນວນບໍລິການ: item.services,
        ຄ່າຄອມມິດຊັ່ນ: new Intl.NumberFormat("lo-LA").format(item.commission) + " ₭",
        ຄວາມພຶງພໍໃຈ: `${item.satisfaction}%`,
      }));
      const employeeSheet = XLSX.utils.json_to_sheet(employeeSheetData);
      XLSX.utils.book_append_sheet(workbook, employeeSheet, "ລາຍງານພະນັກງານ");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(data, `report_${startDate}_to_${endDate}.xlsx`);
      message.success("ສົ່ງອອກ Excel ສຳເລັດ");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ Excel");
    } finally {
      setExportLoading(false);
    }
  };

  // const exportToPDF = () => {
  //   if (!revenueData.length && !serviceData.length && !employeeData.length) {
  //     message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
  //     return;
  //   }

  //   setExportLoading(true);
  //   try {
  //     const doc = new jsPDF();

  //     doc.setFontSize(16);
  //     doc.text(`ລາຍງານ (${startDate} ເຖິງ ${endDate})`, 14, 20);

  //     doc.setFontSize(14);
  //     doc.text("ລາຍງານລາຍຮັບ", 14, 30);
  //     autoTable(doc, {
  //       startY: 35,
  //       head: [["ວັນທີ", "ລາຍຮັບ (₭)", "ຈຳນວນລູກຄ້າ"]],
  //       body: [
  //         [
  //           "ສະຫຼຸບ",
  //           new Intl.NumberFormat("lo-LA").format(revenueStats.totalRevenue) + " ₭",
  //           revenueStats.totalCustomers.toString(),
  //         ],
  //         ...revenueData.map((item) => [
  //           item.date,
  //           new Intl.NumberFormat("lo-LA").format(item.revenue) + " ₭",
  //           item.customers.toString(),
  //         ]),
  //       ],
  //       styles: { fontSize: 10 },
  //       headStyles: { fillColor: [102, 126, 234] },
  //     });

  //     doc.addPage();
  //     doc.setFontSize(14);
  //     doc.text("ລາຍງານບໍລິການ", 14, 20);
  //     autoTable(doc, {
  //       startY: 25,
  //       head: [["ບໍລິການ", "ຈຳນວນຄັ້ງ", "ລາຍຮັບ (₭)"]],
  //       body: serviceData.map((item) => [
  //         item.name,
  //         item.count.toString(),
  //         new Intl.NumberFormat("lo-LA").format(item.revenue) + " ₭",
  //       ]),
  //       styles: { fontSize: 10 },
  //       headStyles: { fillColor: [102, 126, 234] },
  //     });

  //     doc.addPage();
  //     doc.setFontSize(14);
  //     doc.text("ລາຍງານພະນັກງານ", 14, 20);
  //     autoTable(doc, {
  //       startY: 25,
  //       head: [["ຊື່", "ຈຳນວນບໍລິການ", "ຄ່າຄອມມິດຊັ່ນ (₭)", "ຄວາມພຶງພໍໃຈ"]],
  //       body: employeeData.map((item) => [
  //         item.name,
  //         item.services.toString(),
  //         new Intl.NumberFormat("lo-LA").format(item.commission) + " ₭",
  //         `${item.satisfaction}%`,
  //       ]),
  //       styles: { fontSize: 10 },
  //       headStyles: { fillColor: [102, 126, 234] },
  //     });

  //     doc.save(`report_${startDate}_to_${endDate}.pdf`);
  //     message.success("ສົ່ງອອກ PDF ສຳເລັດ");
  //   } catch (error) {
  //     console.error("Error exporting to PDF:", error);
  //     message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ PDF");
  //   } finally {
  //     setExportLoading(false);
  //   }
  // };

  // Render Functions
  
  
  
  const renderRevenueReport = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={<Text style={{ color: "rgba(255,255,255,0.85)" }}>ລາຍຮັບລວມ</Text>}
              value={revenueStats.totalRevenue}
              suffix="₭"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<DollarOutlined />}
            />
            {/* <Divider style={{ margin: "12px 0", borderColor: "rgba(255,255,255,0.2)" }} /> */}
            {/* <Space>
              <RiseOutlined style={{ color: "#52c41a" }} />
              <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                +12.5% ຈາກເດືອນກ່ອນ
              </Text>
            </Space> */}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={<Text style={{ color: "rgba(255,255,255,0.85)" }}>ລູກຄ້າທັງໝົດ</Text>}
              value={revenueStats.totalCustomers}
              suffix="ຄົນ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<UserOutlined />}
            />
            {/* <Divider style={{ margin: "12px 0", borderColor: "rgba(255,255,255,0.2)" }} /> */}
            {/* <Space>
              <RiseOutlined style={{ color: "#52c41a" }} />
              <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                +8.3% ຈາກເດືອນກ່ອນ
              </Text>
            </Space> */}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລາຍຮັບສະເລ່ຍຕໍ່ມື້
                </Text>
              }
              value={revenueStats.avgRevenuePerDay}
              suffix="₭"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<CalendarOutlined />}
            />
            {/* <Divider style={{ margin: "12px 0", borderColor: "rgba(255,255,255,0.2)" }} /> */}
            {/* <Space>
              <RiseOutlined style={{ color: "#52c41a" }} />
              <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                +5.7% ຈາກເດືອນກ່ອນ
              </Text>
            </Space> */}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລາຍຮັບສະເລ່ຍຕໍ່ລູກຄ້າ
                </Text>
              }
              value={revenueStats.avgRevenuePerCustomer}
              suffix="₭"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<TrophyOutlined />}
            />
            {/* <Divider style={{ margin: "12px 0", borderColor: "rgba(255,255,255,0.2)" }} /> */}
            {/* <Space>
              <FallOutlined style={{ color: "#ff4d4f" }} />
              <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                -2.1% ຈາກເດືອນກ່ອນ
              </Text>
            </Space> */}
          </Card>
        </Col>
      </Row>

      {/* Revenue Chart */}
      <Card
        title={
          <Space>
            <LineChartOutlined style={{ color: "#667eea" }} />
            <Text strong>ກຣາບສະແດງລາຍຮັບ</Text>
          </Space>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        {revenueError ? (
          <Alert
            message="ເກີດຂໍ້ຜິດພາດໃນການໂຫລດລາຍງານລາຍຮັບ"
            type="error"
            showIcon
          />
        ) : revenueLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#667eea" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="colorCustomers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#52c41a" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#8c8c8c" />
              <YAxis stroke="#8c8c8c" />
              <Tooltip
                contentStyle={{
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                }}
                formatter={(value, name) => [
                  name === "revenue"
                    ? new Intl.NumberFormat("lo-LA").format(value as number) + " ₭"
                    : `${value} ຄົນ`,
                  name === "revenue" ? "ລາຍຮັບ" : "ລູກຄ້າ",
                ]}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#667eea"
                fillOpacity={1}
                fill="url(#colorRevenue)"
                strokeWidth={3}
                name="ລາຍຮັບ"
              />
              <Area
                type="monotone"
                dataKey="customers"
                stroke="#52c41a"
                fillOpacity={1}
                fill="url(#colorCustomers)"
                strokeWidth={3}
                name="ລູກຄ້າ"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>
    </Space>
  );

  const renderServiceReport = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card
          title={
            <Space>
              <PieChartOutlined style={{ color: "#667eea" }} />
              <Text strong>ສັດສ່ວນບໍລິການ</Text>
            </Space>
          }
          bordered={false}
          style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
        >
          {roomUsageError ? (
            <Alert message="ເກີດຂໍ້ຜິດພາດໃນການໂຫລດຂໍ້ມູນຫ້ອງ" type="error" showIcon />
          ) : roomUsageLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 350,
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={serviceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    // label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {serviceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Divider />
              <Space direction="vertical" style={{ width: "100%" }}>
                {serviceData.map((item, index) => (
                  <Row key={index} justify="space-between" align="middle">
                    <Col>
                      <Space>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            background: item.color,
                          }}
                        />
                        <Text>{item.name}</Text>
                      </Space>
                    </Col>
                    <Col>
                      <Tag color={item.color}>{item.count} ຄັ້ງ</Tag>
                    </Col>
                  </Row>
                ))}
              </Space>
            </>
          )}
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card
          title={
            <Space>
              <BarChartOutlined style={{ color: "#667eea" }} />
              <Text strong>ລາຍຮັບຕາມການບໍລິການ</Text>
            </Space>
          }
          bordered={false}
          style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
        >
          {roomUsageError ? (
            <Alert message="ເກີດຂໍ້ຜິດພາດໃນການໂຫລດຂໍ້ມູນຫ້ອງ" type="error" showIcon />
          ) : roomUsageLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 300,
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={serviceData}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#667eea" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#764ba2" stopOpacity={0.9} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#8c8c8c" />
                <YAxis stroke="#8c8c8c" />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                  }}
                  formatter={(value) => [
                    new Intl.NumberFormat("lo-LA").format(value as number) + " ₭",
                    "ລາຍຮັບ",
                  ]}
                />
                <Bar dataKey="revenue" fill="url(#barGradient)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Col>
    </Row>
  );

  const renderEmployeeReport = () => (
    <Card
      title={
        <Space>
          <TrophyOutlined style={{ color: "#667eea" }} />
          <Text strong>ລາຍງານປະສິດທິພາບພະນັກງານ</Text>
        </Space>
      }
      bordered={false}
      style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
    >
      {employeeError ? (
        <Alert
          message="ເກີດຂໍ້ຜິດພາດໃນການໂຫລດລາຍງານປະສິດທິພາບພະນັກງານ"
          type="error"
          showIcon
        />
      ) : employeeLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Table
          dataSource={employeeData}
          columns={[
            {
              title: "ອັນດັບ",
              key: "rank",
              width: 80,
              align: "center",
              render: (_, __, index) => (
                <Tag
                  color={index === 0 ? "gold" : index === 1 ? "silver" : index === 2 ? "#cd7f32" : "default"}
                  style={{ fontSize: 14, fontWeight: "bold" }}
                >
                  #{index + 1}
                </Tag>
              ),
            },
            {
              title: "ພະນັກງານ",
              dataIndex: "name",
              key: "name",
              render: (text) => (
                <Space>
                  <UserOutlined style={{ color: "#667eea" }} />
                  <Text strong>{text}</Text>
                </Space>
              ),
            },
            {
              title: "ຈຳນວນບໍລິການ",
              dataIndex: "services",
              key: "services",
              sorter: (a: any, b: any) => a.services - b.services,
              render: (value) => <Tag color="blue">{value} ຄັ້ງ</Tag>,
            },
            {
              title: "ຄ່າຄອມມິດຊັ່ນ",
              dataIndex: "commission",
              key: "commission",
              render: (value: number) => (
                <Text strong style={{ color: "#52c41a" }}>
                  {new Intl.NumberFormat("lo-LA").format(value)} ₭
                </Text>
              ),
              sorter: (a: any, b: any) => a.commission - b.commission,
            },
            {
              title: "ຄະແນນຄວາມພຶງພໍໃຈ",
              dataIndex: "satisfaction",
              key: "satisfaction",
              render: (value: number) => (
                <Space>
                  <Tag color={value >= 90 ? "success" : value >= 70 ? "warning" : "error"}>
                    {value}%
                  </Tag>
                  {value >= 90 && <TrophyOutlined style={{ color: "#faad14" }} />}
                </Space>
              ),
              sorter: (a: any, b: any) => a.satisfaction - b.satisfaction,
            },
          ]}
          pagination={false}
          style={{ marginTop: 16 }}
        />
      )}
    </Card>
  );

  const renderCurrentReport = () => {
    switch (reportType) {
      case "revenue":
        return renderRevenueReport();
      case "service":
        return renderServiceReport();
      case "employee":
        return renderEmployeeReport();
      default:
        return renderRevenueReport();
    }
  };

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            📊 ລາຍງານ
          </Title>
          <Text type="secondary">ວິເຄາະຂໍ້ມູນ ແລະ ປະສິດທິພາບທຸລະກິດ</Text>
        </Col>
      </Row>

      {/* Control Panel */}
      <Card
        bordered={false}
        style={{
          marginBottom: 24,
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={10}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ປະເພດລາຍງານ
              </Text>
              <Segmented
                value={reportType}
                onChange={setReportType}
                options={[
                  {
                    label: (
                      <Space>
                        <DollarOutlined />
                        <span>ລາຍຮັບ</span>
                      </Space>
                    ),
                    value: "revenue",
                  },
                  {
                    label: (
                      <Space>
                        <BarChartOutlined />
                        <span>ບໍລິການ</span>
                      </Space>
                    ),
                    value: "service",
                  },
                  {
                    label: (
                      <Space>
                        <UserOutlined />
                        <span>ພະນັກງານ</span>
                      </Space>
                    ),
                    value: "employee",
                  },
                ]}
                block
                style={{ width: "100%" }}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={7}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ຊ່ວງເວລາ
              </Text>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={7}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ສົ່ງອອກລາຍງານ
              </Text>
              <Space style={{ width: "100%" }}>
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  onClick={exportToExcel}
                  loading={exportLoading}
                  disabled={revenueLoading || roomUsageLoading || employeeLoading}
                  style={{
                    background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                    border: "none",
                  }}
                >
                  Excel
                </Button>
                {/* <Button
                  icon={<FilePdfOutlined />}
                  onClick={exportToPDF}
                  loading={exportLoading}
                  disabled={revenueLoading || roomUsageLoading || employeeLoading}
                  style={{ borderColor: "#ff4d4f", color: "#ff4d4f" }}
                >
                  PDF
                </Button> */}
              </Space>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Report Content */}
      {renderCurrentReport()}
    </div>
  );
};

export default ReportsPage;